import { defineStore } from 'pinia';
import { getKbaQuestionsApi, sendKbaAnswersApi } from '@Api/kba.api';
import type { KbaUserInfo, KbaQuestion, KbaAnswer, KbaQuestionSet, KbaAnswersResponse } from '@Types/kba.types';
import { KbaStatusCode } from '@Enums/kbaStatusCode.enum';

export const useKbaStore = defineStore('kba', {
    state: () => ({
        questions: [] as KbaQuestion[],
        expires: null as string | null,
        selectedAnswers: {} as Record<string, string>,
        attempts: 0,
        attemptsLeft: 0,
        canRetry: true,
        loading: false,
        failed: false,
        timeExpired: false,
        noMoreChances: false,
        kbaUserInfo: null as KbaUserInfo | null,
        kbaStatusCode: null as KbaStatusCode | null,
    }),

    getters: {
        allAnswered: (state): boolean =>
            state.questions.length > 0 &&
            state.questions.every(q => Boolean(state.selectedAnswers[q.id])),

        currentStatusCode: (state): KbaStatusCode | null => state.kbaStatusCode,
    },

    actions: {
        async fetchQuestions(userInfo: KbaUserInfo): Promise<KbaQuestionSet | null> {
            this.loading = true;
            this.failed = false;
            this.noMoreChances = false;
            this.kbaUserInfo = userInfo;
            try {
                const questionSet = await getKbaQuestionsApi(userInfo);
                this._applyQuestionSet(questionSet);
                return questionSet;
            } catch (error) {
                console.error('[kba.store] fetchQuestions error:', error);
                this.failed = true;
                return null;
            } finally {
                this.loading = false;
            }
        },

        async submitAnswers(): Promise<KbaAnswersResponse | null> {
            if (!this.kbaUserInfo) throw new Error('[kba.store] submitAnswers called without kbaUserInfo');
            this.loading = true;
            try {
                const answersPayload: KbaAnswer[] = Object.entries(this.selectedAnswers).map(
                    ([questionId, answerId]) => ({ questionId, answerId })
                );
                const response = await sendKbaAnswersApi({
                    kbaUserInfo: this.kbaUserInfo,
                    answers: answersPayload,
                });
                this._handleAnswersResponse(response);
                return response;
            } catch (error) {
                console.error('[kba.store] submitAnswers error:', error);
                this.failed = true;
                return null;
            } finally {
                this.loading = false;
            }
        },

        _handleAnswersResponse(response: KbaAnswersResponse): void {
            this.kbaStatusCode = response.kbaStatusCode;
            this.attemptsLeft = response.attemptsLeft;
            this.canRetry = response.canRetry;

            if (response.timeLimitReached) {
                this.timeExpired = true;
            }

            if (response.isPassed) {
                this.noMoreChances = true;
                return;
            }

            if (!response.canRetry) {
                this.noMoreChances = true;
                return;
            }

            // Follow-up or retry questions
            if (response.questions) {
                this.attempts += 1;
                this._applyQuestionSet(response.questions);
            }
        },

        _applyQuestionSet(questionSet: KbaQuestionSet): void {
            this.questions = questionSet.questions;
            this.expires = questionSet.expires;
            this.attemptsLeft = questionSet.attemptsLeft;
            this.canRetry = questionSet.canRetry;
            this.kbaStatusCode = questionSet.kbaStatusCode;
            this.selectedAnswers = {};
            this.timeExpired = false;
        },

        selectAnswer(questionId: string, answerId: string): void {
            this.selectedAnswers = { ...this.selectedAnswers, [questionId]: answerId };
        },

        retry(): void {
            this.failed = false;
            this.timeExpired = false;
            this.selectedAnswers = {};
        },

        reset(): void {
            this.questions = [];
            this.expires = null;
            this.selectedAnswers = {};
            this.attempts = 0;
            this.attemptsLeft = 0;
            this.canRetry = true;
            this.loading = false;
            this.failed = false;
            this.timeExpired = false;
            this.noMoreChances = false;
            this.kbaUserInfo = null;
            this.kbaStatusCode = null;
        },

    },
});
